import React, { useState, useCallback, useEffect, createRef } from "react";
import { Link, useHistory } from "react-router-dom";
import { CheckCircle } from "react-feather";
import { useIntl } from "react-intl";
import logo from "../../assets/images/buscar-logo-light-2.png";
import { api } from "../../api";
import { validateEmail, validateCpf } from "../../utils/validate";
import "./styles.scss";

const SignUp = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [cpf, setCpf] = useState({ value: "", raw: "" });
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successfulSignup, setSuccessfulSignup] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const { formatMessage: f } = useIntl();
  const history = useHistory();

  let nameInput = createRef();

  useEffect(() => {
    nameInput.current && nameInput.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasAllRequiredFields = () =>
    name.length !== 0 &&
    email.length !== 0 &&
    cpf.value.length !== 0 &&
    password.length !== 0 &&
    passwordConfirmation.length !== 0 &&
    hasAcceptedTerms;

  const generateRequiredFieldsMessage = () => {
    const errorFields = [];
    if (name.length === 0) errorFields.push("nome");
    if (email.length === 0) errorFields.push("e-mail");
    if (cpf.value.length === 0) errorFields.push("CPF");
    if (password.length === 0) errorFields.push("senha");
    if (passwordConfirmation.length === 0)
      errorFields.push("confirmação de senha");

    return (
      "Informe " +
      errorFields.reduce(
        (prev, field, index) =>
          prev + (index === errorFields.length - 1 ? " e " : ", ") + field
      )
    );
  };

  const hangleSignUpSubmit = e => {
    e.preventDefault();

    if (!hasAllRequiredFields()) {
      setErrorMessage(generateRequiredFieldsMessage());
    } else if (!validateEmail(email)) {
      setErrorMessage(f({ id: "email-invalido" }));
    } else if (!validateCpf(cpf.raw)) {
      setErrorMessage(f({ id: "cpf-invalido" }));
    } else if (password !== passwordConfirmation) {
      setErrorMessage(f({ id: "senhas-diferentes" }));
    } else {
      setIsLoading(true);
      api
        .post("/users/signup", {
          email,
          cpf: cpf.value,
          nome: name,
          senha: password
        })
        .then(res => {
          setSuccessfulSignup(true);
        })
        .catch(error => {
          setErrorMessage(error.response.data.message || f({ id: "erro-api" }));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleCpfChange = useCallback(
    unformattedCpf => {
      const raw = unformattedCpf.replace(/([^\d])/g, "");
      const value = raw.replace(
        /^(\d{3})(\d{3})(\d{3})(\d{2})(.*)/,
        "$1.$2.$3-$4"
      );
      setCpf({ value, raw });
    },
    [setCpf]
  );

  if (successfulSignup) {
    return (
      <>
        <div className="logo has-title">
          <img alt="BusCAR logo" src={logo} />
          <div className="auth-title">{f({ id: "cadastro" })}</div>
        </div>
        <div className="successful-signup">
          <CheckCircle className="check" size="32px" />
          <p>{f({ id: "cadastro-sucesso" })}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="logo has-title">
        <a href = "/">
          <img alt="BusCAR logo" src={logo} />
        </a>
        <div className="auth-title">{f({ id: "cadastro" })}</div>
      </div>
      {errorMessage ? (
        <div className="auth-error-message">{errorMessage}</div>
      ) : (
        <div className="auth-error-message-spacer"></div>
      )}

      <div className="input-container">
        <input
          ref={nameInput}
          type="text"
          name="name"
          placeholder={f({ id: "nome" })}
          value={name}
          required
          onChange={e => setName(e.target.value)}
        />
        <label htmlFor="name">{f({ id: "nome" })}</label>
      </div>

      <div className="input-container">
        <input
          type="email"
          name="email"
          placeholder="E-mail"
          value={email}
          required
          onChange={e => setEmail(e.target.value)}
        />
        <label htmlFor="email">E-mail</label>
      </div>

      <div className="input-container">
        <input
          name="cpf"
          placeholder="CPF"
          value={cpf.value}
          required
          onChange={e => handleCpfChange(e.target.value)}
        />
        <label htmlFor="cpf">CPF</label>
      </div>

      <div className="input-container">
        <input
          type="password"
          name="password"
          placeholder={f({ id: "senha" })}
          value={password}
          required
          onChange={e => setPassword(e.target.value)}
        />
        <label htmlFor="password">{f({ id: "senha" })}</label>
      </div>

      <div className="input-container">
        <input
          className={
            passwordConfirmation.length > 0 && passwordConfirmation !== password
              ? "error"
              : ""
          }
          type="password"
          name="password-confirmation"
          placeholder={f({ id: "confirma-senha" })}
          value={passwordConfirmation}
          required
          onChange={e => setPasswordConfirmation(e.target.value)}
        />
        <label htmlFor="password-confirmation">
          {f({ id: "confirma-senha" })}
        </label>
      </div>

      <div className="politica-privacidade">
        <label>
          <input
            data-testid="accept-terms"
            type="checkbox"
            checked={hasAcceptedTerms}
            onChange={e => setHasAcceptedTerms(e.target.checked)}
          />{" "}
          <span>
            {f({ id: "li-e-aceito" })}{" "}
            <Link to="/terms-of-use" target="_blank">
              {f({ id: "termos-uso" })}
            </Link>{" "}
            {f({ id: "e-a" })}{" "}
            <Link to="/privacy-policy" target="_blank">
              {f({ id: "politica-privacidade" })}
            </Link>
            .
          </span>
        </label>
      </div>
      <button
        className={`button primary ${isLoading ? "is-loading" : ""}`}
        disabled={!hasAllRequiredFields()}
        onClick={hangleSignUpSubmit}
      >
        {f({ id: "cadastrar" })}
      </button>
      <div className="links">
        <a href="/#" onClick={() => history.goBack()}>
          {f({ id: "voltar" })}
        </a>
      </div>
    </>
  );
};

export default SignUp;
